import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { logger } from "../lib/logger";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated — you can now sign in");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      logger.error("Reset password failed:", err);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset password");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div data-testid="reset-password-page" className="px-6 md:px-12 lg:px-24 py-24 max-w-md mx-auto text-center">
        <p className="label-eyebrow text-[#555]">— Reset password</p>
        <h1 className="font-serif-display text-5xl tracking-tighter font-light mt-4">
          Missing reset link
        </h1>
        <p className="text-[#555] mt-6">Please use the link from your email.</p>
        <Link
          to="/forgot-password"
          className="inline-block mt-8 label-eyebrow border border-[#111] px-8 py-4 hover:bg-[#111] hover:text-white transition-colors"
        >
          Request a new link
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="reset-password-page" className="px-6 md:px-12 lg:px-24 py-24 max-w-md mx-auto">
      <p className="label-eyebrow text-[#555]">— Reset password</p>
      <h1 className="font-serif-display text-5xl tracking-tighter leading-none font-light mt-4">
        Choose a new password
      </h1>

      {done ? (
        <div data-testid="reset-success" className="mt-10 border border-[#1E3A8A]/30 bg-[#F4EFE6]/40 p-6">
          <p className="font-serif-display text-2xl text-[#111]">
            <span className="italic text-[#1E3A8A]">Updated.</span>
          </p>
          <p className="text-sm text-[#333] mt-3">Redirecting you to the sign-in page…</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-10 space-y-6">
          <div>
            <label className="label-eyebrow text-[#555]">New password</label>
            <Input
              data-testid="reset-password-new"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-[#111]"
            />
            <p className="text-xs text-[#888] font-mono-aam mt-1">Minimum 8 characters</p>
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Confirm</label>
            <Input
              data-testid="reset-password-confirm"
              type="password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-[#111]"
            />
          </div>
          <Button
            type="submit"
            data-testid="reset-submit"
            disabled={submitting || !password || !confirm}
            className="w-full rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white py-6 h-auto label-eyebrow"
          >
            {submitting ? "Updating…" : "Update password"}
          </Button>
        </form>
      )}
    </div>
  );
}
